/* eslint-disable prettier/prettier */

// library imports
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

// customized menu imports
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import { AuthenticationContext, ImagesContext } from "../Context";
import IntegrationStatusModal from "../integrations/IntegrationStatusModal";
import { getHeaders } from "../utils/HeaderHelper";
import { checkPrivileges } from "../utils/PrivilegesHelper";
import { PrivilegesContext } from "../PrivilegesContext";

interface Props {
  user: any;
}

const SHOW_TERMS_AND_CONDITIONS = false;

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {},
}));

const ProfileMenu: React.FC<Props> = (props: any) => {
  const images = React.useContext(ImagesContext);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const userPrivileges = React.useContext(PrivilegesContext);

  // customized menu options
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showIntegrationModal, setShowIntegrationModal] = React.useState(false);
  const [activeIntegrations, setActiveIntegrations] = React.useState([]);
  const [inactiveIntegrations, setInactiveIntegrations] = React.useState([]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const openIntegrationModal = () => setShowIntegrationModal(true);
  const closeIntegrationModal = () => setShowIntegrationModal(false);

  const fetchIntegrations = () => {
    fetch("/integrations", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setActiveIntegrations(result.resource.active_integrations);
          setInactiveIntegrations(result.resource.inactive_integrations);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    fetchIntegrations();
  }, []);

  return (
    <div id="profile-menu-container">
      {checkPrivileges(
        userPrivileges,
        "Enable Third Party API Notification Center"
      ) && (
        <Button
          id="profile-menu-button"
          variant="contained"
          disableElevation
          style={{
            marginRight: "0px",
            position: "relative",
          }}
          onClick={openIntegrationModal}
        >
          <div className="hex-container">
            <img src={images.bell_icon} alt="ndhin-status" />
            {inactiveIntegrations.length > 0 && (
              <span className="notification-badge" />
            )}
          </div>
        </Button>
      )}
      <Button
        id="profile-menu-button"
        aria-controls={open ? "profile-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        endIcon={open ? <ArrowDropUp /> : <ArrowDropDown fontSize="large" />}
      >
        <div className="hex-container">
          <span className="letter">
            {props.user.name && props.user.name[0]}
          </span>
        </div>
      </Button>
      <StyledMenu
        id="profile-menu"
        MenuListProps={{
          "aria-labelledby": "profile-menu-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {props.user.role != "patient" && (
          <RouterLink to="/profile-edit">
            <MenuItem onClick={handleClose} disableRipple>
              EDIT PROFILE
            </MenuItem>
          </RouterLink>
        )}
        {SHOW_TERMS_AND_CONDITIONS && (
          <>
            <Divider />
            <a rel="nofollow" href="/terms">
              <MenuItem onClick={handleClose} disableRipple>
                TERMS & CONDITIONS
              </MenuItem>
            </a>
            <Divider />
          </>
        )}

        <a rel="nofollow" data-method="delete" href="/users/sign_out">
          <MenuItem onClick={handleClose} disableRipple>
            LOG OUT
          </MenuItem>
        </a>
      </StyledMenu>

      {openIntegrationModal && (
        <IntegrationStatusModal
          showIntegrationModal={showIntegrationModal}
          closeIntegrationModal={closeIntegrationModal}
          activeIntegrations={activeIntegrations}
          inactiveIntegrations={inactiveIntegrations}
        />
      )}
    </div>
  );
};

export default ProfileMenu;
